import type { ShapeType } from '@/lib/shapes/types/shape-type'
import type { PathCommand } from '@/lib/elements/types/path-command'

/** 0–100 (% of max corner radius for the current box). */
export const ROUNDING_MIN = 0
export const ROUNDING_MAX = 100

export const ROUNDING_RANGE_ERROR = 'Value: 0-100'

/** Max corner radius in box units: half the shorter side at 100%. */
export function roundedCornerRadiusFromPercent(
  width: number,
  height: number,
  percent: number
): number {
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const maxR = Math.min(w, h) / 2
  const p = Math.max(ROUNDING_MIN, Math.min(ROUNDING_MAX, percent))

  return (p / 100) * maxR
}

/** Treat as square when side lengths match within this (pt); avoids float noise on small boxes. */
const SQUARE_EPS = 0.02

/**
 * SVG path for a rounded rectangle in local 0..width × 0..height space.
 *
 * Uses **elliptical arc (`A`)** segments so corners are true quarter-circles (same idea as CSS
 * `border-radius`), not quadratic beziers — which read as a “squircle” and never look like a full
 * circle at 100% on a square.
 *
 * `radius` is clamped to half the shorter side. For non-squares at 100%, the outline is a “pill”
 * (straight sides on the long axis); only a **square** becomes a perfect **circle** at max radius.
 */
export function roundedRectPathD(width: number, height: number, radius: number): string {
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const r = Math.max(0, Math.min(radius, w / 2, h / 2))

  if (r <= 0) {
    return `M 0 0 L ${w} 0 L ${w} ${h} L 0 ${h} Z`
  }

  // Square at max corner radius: single closed circle (legacy app / design-tool behavior at 100%).
  if (Math.abs(w - h) < SQUARE_EPS && Math.abs(r - Math.min(w, h) / 2) < SQUARE_EPS) {
    const cx = w / 2
    const cy = h / 2

    return `M ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} Z`
  }

  // Rounded rect with true circular corners (y-down, clockwise). Matches border-radius math.
  return [
    `M ${r} 0`,
    `L ${w - r} 0`,
    `A ${r} ${r} 0 0 1 ${w} ${r}`,
    `L ${w} ${h - r}`,
    `A ${r} ${r} 0 0 1 ${w - r} ${h}`,
    `L ${r} ${h}`,
    `A ${r} ${r} 0 0 1 0 ${h - r}`,
    `L 0 ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    'Z',
  ].join(' ')
}

export function parseRoundingPercent(raw: string): { ok: true; value: number } | { ok: false } {
  const t = raw
    .trim()
    .replace(/%+\s*$/u, '')
    .trim()

  if (t === '') return { ok: false }
  if (!/^-?\d+(\.\d+)?$/u.test(t)) return { ok: false }
  const n = Number(t)

  if (!Number.isFinite(n)) return { ok: false }
  return { ok: true, value: n }
}

/**
 * Kind id 1 = rectangle (API `rectangleElement`). Shapes created locally may omit `kindId` until
 * loaded from checkpoint; `square` maps to 1 in `buildStowedElement`.
 */
export function effectiveShapeKindId(
  shape: { kindId?: number; type: string } | null | undefined
): number {
  if (shape == null) return 0
  if (typeof shape.kindId === 'number' && Number.isFinite(shape.kindId)) return shape.kindId
  return shape.type === 'square' ? 1 : 2
}

/** Rounding only applies to rectangle/square (kindId 1) shapes — see `effectiveShapeKindId`. */
const ROUNDING_ELIGIBLE_TYPES: ReadonlySet<ShapeType> = new Set(['square'])

export function isRoundingEligibleShapeType(type: ShapeType): boolean {
  return ROUNDING_ELIGIBLE_TYPES.has(type)
}

export function isRoundingEligibleShape(shape: { type: string } | null | undefined): boolean {
  if (shape == null) return false
  return isRoundingEligibleShapeType(shape.type as ShapeType)
}

type XY = { x: number; y: number }

function pointsFromClosedLinePath(commands: PathCommand[]): XY[] | null {
  if (!Array.isArray(commands) || commands.length < 3) return null
  const pts: XY[] = []

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      pts.push({ x: cmd.moveTo.x, y: cmd.moveTo.y })
      continue
    }

    if ('lineTo' in cmd) {
      pts.push({ x: cmd.lineTo.x, y: cmd.lineTo.y })
      continue
    }

    return null
  }

  if (pts.length < 3) return null
  return pts
}

function clampCornerOffset(
  radius: number,
  edgeLenA: number,
  edgeLenB: number,
  angle: number
): number {
  const half = angle / 2
  const t = Math.tan(half)

  if (!Number.isFinite(t) || t <= 1e-6) return 0
  const byRadius = radius / t

  return Math.max(0, Math.min(byRadius, edgeLenA / 2, edgeLenB / 2))
}

/**
 * Build rounded path for a closed polygon made of move/line commands.
 * Returns null for unsupported paths (curves, degenerate geometry, or ineligible type).
 */
export function roundedPathDFromLineCommands(
  shapeType: ShapeType,
  commands: PathCommand[] | undefined,
  width: number,
  height: number,
  percent: number
): string | null {
  if (!isRoundingEligibleShapeType(shapeType)) return null
  if (!Array.isArray(commands) || commands.length < 3) return null

  const points = pointsFromClosedLinePath(commands)

  if (!points || points.length < 3) return null

  const radius = roundedCornerRadiusFromPercent(width, height, percent)

  if (!Number.isFinite(radius) || radius <= 0) return null

  const n = points.length
  const starts: XY[] = []
  const ends: XY[] = []

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n]
    const curr = points[i]
    const next = points[(i + 1) % n]
    const vIn = { x: prev.x - curr.x, y: prev.y - curr.y }
    const vOut = { x: next.x - curr.x, y: next.y - curr.y }
    const lenIn = Math.hypot(vIn.x, vIn.y)
    const lenOut = Math.hypot(vOut.x, vOut.y)

    if (lenIn < 1e-6 || lenOut < 1e-6) return null

    const inUnit = { x: vIn.x / lenIn, y: vIn.y / lenIn }
    const outUnit = { x: vOut.x / lenOut, y: vOut.y / lenOut }
    const cosTheta = Math.max(-1, Math.min(1, inUnit.x * outUnit.x + inUnit.y * outUnit.y))
    const angle = Math.acos(cosTheta)

    if (!Number.isFinite(angle) || angle <= 1e-3 || Math.abs(Math.PI - angle) <= 1e-3) return null

    const offset = clampCornerOffset(radius, lenIn, lenOut, angle)

    if (offset <= 0) return null

    starts.push({ x: curr.x + inUnit.x * offset, y: curr.y + inUnit.y * offset })
    ends.push({ x: curr.x + outUnit.x * offset, y: curr.y + outUnit.y * offset })
  }

  const parts: string[] = [`M ${ends[0].x} ${ends[0].y}`]

  for (let i = 0; i < n; i++) {
    parts.push(`L ${starts[(i + 1) % n].x} ${starts[(i + 1) % n].y}`)
    const corner = points[(i + 1) % n]
    const end = ends[(i + 1) % n]

    parts.push(`Q ${corner.x} ${corner.y} ${end.x} ${end.y}`)
  }

  parts.push('Z')

  return parts.join(' ')
}
